import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  ModeratedTaskFormat,
  Participation,
  StudyType,
  useTaskMeeting,
} from '@app/api';
import { LabeledIconChip, LabeledSection, useLinkToMeeting } from '@app/core';
import { SharableLink } from '@app/form';
import { LinkToMeetingButton } from '../../components/LinkToMeetingButton';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  linkContainer: {
    display: 'flex',
    gap: theme.marginBase * 2,
    width: '100%',
    [theme.media.mobile]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  link: {
    flex: 1,
    [theme.media.mobile]: {
      flex: 'none',
      width: '100%',
    },
  },

  inFlexBlock: {
    '&:last-child': {
      marginRight: 0,
    },
  },
  timezone: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
}));

interface Props {
  participation: Participation;
  isDeviceInvalid?: boolean;
}

export const AppointmentMeetingLink = ({
  participation,
  isDeviceInvalid,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const { participant, session, appointment } = participation;

  const link = useLinkToMeeting(participation.id);
  const { data: meeting } = useTaskMeeting(participation.taskId);

  const format = meeting?.format;
  const isUnModerated =
    participation.session?.studyType === StudyType.UNMODERATED;
  if (
    !session ||
    (session.studyType === StudyType.MODERATED && (!format || !appointment))
  ) {
    return null;
  }

  const isPhysical = format === ModeratedTaskFormat.PHYSICAL_MEETING;
  const isPhone = format === ModeratedTaskFormat.PHONE_CALL;

  return (
    <>
      {link && !isPhysical && !isPhone && (
        <LabeledSection
          label={
            isUnModerated
              ? t('booked.summary.linkToTheStudy')
              : t('booked.summary.videoCallLink')
          }
        >
          <div className={classes.linkContainer}>
            <SharableLink className={classes.link} link={link} />
            <LinkToMeetingButton
              participation={participation}
              fallbackDeviceModal={isDeviceInvalid}
            />
          </div>
        </LabeledSection>
      )}
      {!!meeting?.address && isPhysical && (
        <LabeledSection label={t('booked.summary.address')}>
          <div className={classes.linkContainer}>
            <SharableLink className={classes.link} link={meeting?.address} />
          </div>
        </LabeledSection>
      )}
      {isPhone && participant?.phoneNumber && (
        <LabeledIconChip
          label={t('booked.summary.formatPhoneTitle')}
          chipIcon="ri-phone-line"
          labelColor="inkSecondary"
          color="ink"
          chipLabel={participant.phoneNumber}
        />
      )}
    </>
  );
};
